import React, { useState } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { 
  Filter,
  AlertCircle
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import NotificationItem from '../../components/NotificationItem';
import LoadingSpinner from '../../components/LoadingSpinner';

const Notifications = () => {
  const { notifications, markAllAsRead, clearAllNotifications, loading, unreadCount } = useNotifications();
  const [filter, setFilter] = useState('all');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });
  
  const getFilterCount = (filterType) => {
    return notifications.filter(notification => {
      if (filterType === 'all') return true;
      if (filterType === 'unread') return !notification.read;
      return notification.type === filterType;
    }).length;
  };
  
  const handleMarkAllAsRead = async () => {
    try {
      setIsProcessing(true);
      await markAllAsRead();
    } catch (error) {
      setError('Failed to mark notifications as read. Please try again.');
      console.error('Error marking all as read:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleClearAll = async () => {
    try {
      setIsProcessing(true);
      await clearAllNotifications();
    } catch (error) {
      setError('Failed to clear notifications. Please try again.');
      console.error('Error clearing notifications:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="relative">

      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Notifications</h2>
        <p className="text-gray-600">Stay updated with your campaign status, payments, and more</p>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div className="flex space-x-1 overflow-x-auto pb-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                filter === 'all' 
                  ? 'bg-primary text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              All ({getFilterCount('all')})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                filter === 'unread' 
                  ? 'bg-primary text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Unread ({getFilterCount('unread')})
            </button>
            <button
              onClick={() => setFilter('campaign_status')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                filter === 'campaign_status' 
                  ? 'bg-primary text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Campaigns ({getFilterCount('campaign_status')})
            </button>
            <button
              onClick={() => setFilter('payment')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                filter === 'payment' 
                  ? 'bg-primary text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Payments ({getFilterCount('payment')})
            </button>
          </div>
          
          <div className="flex space-x-2">
            <button 
              onClick={handleMarkAllAsRead}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={getFilterCount('unread') === 0 || isProcessing || loading}
            >
              {isProcessing ? (
                <>
                  <LoadingSpinner size="xs" color="primary" />
                </>
              ) : null}
              Mark all as read
            </button>
            <button 
              onClick={handleClearAll}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={notifications.length === 0 || isProcessing || loading}
            >
              Clear all
            </button>
          </div>
        </div>
        
        <div className="divide-y divide-gray-100">
          {!loading && filteredNotifications.length > 0 ? (
            filteredNotifications.map(notification => (
              <NotificationItem 
                key={notification.id} 
                notification={notification} 
              />
            ))
          ) : (
            <div className="py-16 text-center">
              <div className="inline-block p-4 rounded-full bg-gray-100 mb-4">
                <Filter className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications found</h3>
              <p className="text-gray-500">
                {filter !== 'all' 
                  ? `There are no ${filter === 'unread' ? 'unread' : filter} notifications` 
                  : 'You have no notifications at this time'}
              </p>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Notification Settings
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Customize how and when you receive notifications
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="email_notifications"
                  name="email_notifications"
                  type="checkbox"
                  className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                  defaultChecked
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="email_notifications" className="font-medium text-gray-700">Email notifications</label>
                <p className="text-gray-500">Receive email notifications for important updates</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="push_notifications"
                  name="push_notifications"
                  type="checkbox"
                  className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                  defaultChecked
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="push_notifications" className="font-medium text-gray-700">Push notifications</label>
                <p className="text-gray-500">Receive push notifications on your browser</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="sms_notifications"
                  name="sms_notifications"
                  type="checkbox"
                  className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="sms_notifications" className="font-medium text-gray-700">SMS notifications</label>
                <p className="text-gray-500">Receive SMS notifications for urgent updates</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Notification Types</h4>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="campaign_updates"
                    name="campaign_updates"
                    type="checkbox"
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                    defaultChecked
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="campaign_updates" className="font-medium text-gray-700">Campaign updates</label>
                  <p className="text-gray-500">Status changes, approvals, and review feedback</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="payment_notifications"
                    name="payment_notifications"
                    type="checkbox"
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                    defaultChecked
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="payment_notifications" className="font-medium text-gray-700">Payment notifications</label>
                  <p className="text-gray-500">Payment processing, transfers, and bonuses</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="message_notifications"
                    name="message_notifications"
                    type="checkbox"
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                    defaultChecked
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="message_notifications" className="font-medium text-gray-700">Message notifications</label>
                  <p className="text-gray-500">New messages from brands and platform updates</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="referral_notifications"
                    name="referral_notifications"
                    type="checkbox"
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                    defaultChecked
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="referral_notifications" className="font-medium text-gray-700">Referral notifications</label>
                  <p className="text-gray-500">Referral sign-ups, bonuses, and program updates</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <button
              type="button"
              onClick={() => {
                try {
                  // Simulate saving preferences
                  showSuccess('Notification preferences saved successfully!', 'Your notification settings have been updated.', 4000);
                } catch (error) {
                  console.error('Error saving notification preferences:', error);
                  showError('Failed to save notification preferences.', 'Please check your connection and try again.', 6000);
                }
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Save preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
